import React from 'react';
import styled, { css, useTheme } from 'styled-components';
import { BaseProductCard, Box, Text } from '@sparknz/spark-pattern-library';

import { ImageWithFallback } from 'shared/components';
import { BASE_PATH } from 'shared/constants';
import { useSegment } from 'shared/hooks';
import {
  getFeaturedPromoDeals,
  getPromotionByType,
  PromotionType,
} from 'shared/utils';
import ProductCardPrice from './components/ProductCardPrice';
import ProductGalleryCardDeals from './components/ProductGalleryCardDeals';
import Tag from './components/Tag';
import {
  BODY_M_DEMI_LINE_HEIGHT,
  BODY_S_MEDIUM_LINE_HEIGHT,
} from './constants/spacing.constant';
import { GalleryProductWithPosition } from './typings';
import {
  getDeviceOnlyText,
  getIFPDescriptionText,
  getPreOrderText,
  getPrePriceText,
  getStockText,
  getTitleText,
} from './utils/getDeviceGalleryCardContent.utils';

interface TextProps {
  lineClamp: number;
}

export interface ProductGalleryCardProps {
  product: GalleryProductWithPosition;
  eagerLoadImage?: boolean;
}

const StyledBaseProductCard = styled(BaseProductCard)`
  ${({ theme: { borderRadius } }) => css`
    padding: 0;
    cursor: pointer;
    border-radius: ${borderRadius.round};
  `}
`;

const PreOrderText = styled(Text)`
  min-height: ${BODY_S_MEDIUM_LINE_HEIGHT}px;
`;

const Title = styled(Text)<TextProps>`
  ${({ theme: { media }, lineClamp }) => css`
    ${media.up('slim')} {
      height: ${`${BODY_M_DEMI_LINE_HEIGHT * lineClamp}px`};
      overflow: hidden;
      display: -webkit-box;
      -webkit-line-clamp: ${lineClamp};
      -webkit-box-orient: vertical;
    }
  `}
`;

const DescriptionText = styled(Text)<TextProps>`
  ${({ theme: { media, palette }, lineClamp }) => css`
    ${media.up('slim')} {
      height: ${BODY_S_MEDIUM_LINE_HEIGHT * lineClamp}px;
      overflow: hidden;
      display: -webkit-box;
      -webkit-line-clamp: ${lineClamp};
      -webkit-box-orient: vertical;
    }
    span {
      color: ${palette.baseColors.mawhero};
    }
  `}
`;

const DeviceOnlyText = styled(Text)`
  height: ${BODY_S_MEDIUM_LINE_HEIGHT}px;
  white-space: nowrap;
`;

const StyledImageWithFallback = styled(ImageWithFallback)`
  width: 200px;
  height: 200px;
  object-fit: scale-down;
`;

const StartingFromText = styled(Text)`
  min-height: ${BODY_S_MEDIUM_LINE_HEIGHT}px;
`;

const StockText = styled(Text)`
  min-height: ${BODY_S_MEDIUM_LINE_HEIGHT}px;
`;

const GstText = styled.span`
  ${({
    theme: {
      palette: { baseColors },
    },
  }) => css`
    color: ${baseColors.mawheranui};
  `}
`;

interface PreOrderBandProps {
  hasContent: boolean;
}

const PreOrderBand = styled(Box)<PreOrderBandProps>`
  ${({ theme: { palette }, hasContent }) => css`
    text-align: center;
    background-color: ${hasContent ? `${palette.baseColors.darkPink}16` : null};
  `}
`;

const ProductGalleryCard = ({
  product,
  eagerLoadImage = false,
}: ProductGalleryCardProps) => {
  const {
    palette: { importantSecondary, baseColors },
  } = useTheme();
  const { isBusiness } = useSegment();
  const { promotions, displaySettings } = product || {};

  const {
    shouldShowPreOrderLine,
    shouldShowTitle,
    shouldShowPrePriceLine,
    shouldShowStockLine,
    shouldShowDescription,
    shouldShowDeviceOnly,
    titleLineCount,
    ifpDescriptionLineCount,
  } = displaySettings;

  const promoTag = getPromotionByType(
    promotions,
    PromotionType.MARKETING,
  )?.name;
  const promoDealString = getFeaturedPromoDeals(promotions);
  const deviceOnlyText = getDeviceOnlyText(product, isBusiness);
  const shouldShowDeviceOnlyPriceExclGST = !!deviceOnlyText && isBusiness;
  const preOrderText = getPreOrderText(product);
  const titleText = getTitleText(product);
  const prePriceText = getPrePriceText(product);
  const stockText = getStockText(product);
  const ifpText = getIFPDescriptionText(product, isBusiness);

  const exclGSTText = shouldShowDeviceOnlyPriceExclGST ? (
    <GstText>(excl. GST)</GstText>
  ) : null;

  return (
    <StyledBaseProductCard
      marketingMessage={promoTag}
      dynamicTargetValue={product.offerId}
    >
      <Box
        flexDirection="row"
        justifyContent="center"
        alignItems="center"
        pt="s4"
        pb="s4"
      >
        <StyledImageWithFallback
          loading={eagerLoadImage ? 'eager' : 'lazy'}
          src={product.imageUrl}
          alt={titleText}
          title={titleText}
          fallbackSrc={`${BASE_PATH}/static/assets/fallback/no-image.svg`}
        />
      </Box>
      {shouldShowPreOrderLine ? (
        <PreOrderBand hasContent={!!preOrderText} py="s1" mb="s3">
          <PreOrderText variant="bodySDemi" color={importantSecondary}>
            {preOrderText}
          </PreOrderText>
        </PreOrderBand>
      ) : null}
      <Box flexDirection="column" alignItems="flex-start">
        {product.tag ? (
          <Tag position="absolute" pl="s1" pr="s3" py="s1">
            <Text variant="bodySDemi" color={baseColors.wairua}>
              {product.tag}
            </Text>
          </Tag>
        ) : null}
        <Box px="s4" flexDirection="column">
          {shouldShowTitle ? (
            <Title
              variant="bodyMDemi"
              title={product.name}
              mb="s1"
              lineClamp={titleLineCount}
            >
              {titleText}
            </Title>
          ) : null}
          {shouldShowPrePriceLine ? (
            <StartingFromText variant="bodySMedium">
              {prePriceText}
            </StartingFromText>
          ) : null}
        </Box>
      </Box>
      <ProductCardPrice product={product} />
      <Box px="s4" pb="s4" flexDirection="column">
        {shouldShowStockLine ? (
          <StockText variant="bodySMedium" color={baseColors.marangai} mb="s1">
            {stockText}
          </StockText>
        ) : null}
        {shouldShowDescription ? (
          <DescriptionText
            variant="bodySMedium"
            title="IFP description"
            lineClamp={ifpDescriptionLineCount}
          >
            {ifpText}
          </DescriptionText>
        ) : null}

        {shouldShowDeviceOnly ? (
          <Box pt="s3">
            <DeviceOnlyText variant="bodySMedium" title="Device only price">
              {deviceOnlyText}
              &nbsp;
              {exclGSTText}
            </DeviceOnlyText>
          </Box>
        ) : null}
      </Box>
      <ProductGalleryCardDeals promoDealString={promoDealString} />
    </StyledBaseProductCard>
  );
};

export default React.memo(ProductGalleryCard);
